/**
 * DailyDiarySection Component
 * Main section for displaying and managing daily diary entries
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import DailyEntryCard from './DailyEntryCard';
import AddDailyEntryDialog from './AddDailyEntryDialog';
import projectService from '../../services/projectService';
import notificationService from '../../services/notificationService';

const DailyDiarySection = ({
  projectId,
  project,
  isContractor = false,
  isProjectManager = false,
  isClient = false,
  milestones: rawMilestones = []
}) => {
  const milestones = rawMilestones || [];
  // Both the assigned contractor and the assigned project manager can post updates.
  const canPost = isContractor || isProjectManager;
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedMilestone, setSelectedMilestone] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const entriesPerPage = 10;

  // Load diary entries
  const loadEntries = useCallback(async (pageNum = 1, milestoneFilter = selectedMilestone) => {
    try {
      setLoading(true);
      setError(null);

      const options = {
        limit: entriesPerPage,
        offset: (pageNum - 1) * entriesPerPage
      };

      // Apply milestone filter if in "By Milestone" tab or filter selected
      if (activeTab === 1 && milestoneFilter) {
        options.milestoneId = milestoneFilter;
      }

      const result = await projectService.getDiaryEntries(projectId, options);

      setEntries(Array.isArray(result?.entries) ? result.entries : []);

      // Calculate pagination
      const total = result?.pagination?.total || 0;
      setTotalPages(Math.ceil(total / entriesPerPage) || 1);
      setHasMore(result?.pagination?.hasMore || false);
    } catch (err) {
      console.error('Load diary entries error:', err);
      setError(err.message || 'Failed to load diary entries');
    } finally {
      setLoading(false);
    }
  }, [projectId, activeTab, selectedMilestone]);

  // Initial load and real-time updates
  useEffect(() => {
    loadEntries(page);

    const handleEntryCreated = (data) => {
      if (String(data.projectId) === String(projectId)) {
        loadEntries(1);
        setPage(1);
      }
    };

    const handleEntryUpdated = (data) => {
      if (String(data.projectId) === String(projectId)) {
        loadEntries(page);
      }
    };

    const handleEntryDeleted = (data) => {
      if (String(data.projectId) === String(projectId)) {
        loadEntries(page);
      }
    };

    const unsubscribeCreated = notificationService.on('diary-entry-created', handleEntryCreated);
    const unsubscribeUpdated = notificationService.on('diary-entry-updated', handleEntryUpdated);
    const unsubscribeDeleted = notificationService.on('diary-entry-deleted', handleEntryDeleted);

    return () => {
      unsubscribeCreated();
      unsubscribeUpdated();
      unsubscribeDeleted();
    };
  }, [loadEntries, page, projectId]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(1);
    setSelectedMilestone('');
  };

  // Handle milestone filter change
  const handleMilestoneFilterChange = (event) => {
    const value = event.target.value;
    setSelectedMilestone(value);
    setPage(1);
    loadEntries(1, value);
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
    loadEntries(value);
  };

  // Handle dialog open
  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // Handle entry created
  const handleEntryCreated = (newEntry) => {
    // Reload entries
    setPage(1);
    loadEntries(1);
  };

  // Handle refresh
  const handleRefresh = () => {
    loadEntries(page);
  };

  // Get filtered milestones for current tab
  const getFilteredMilestones = () => {
    if (activeTab === 0) {
      // All entries - show all milestones
      return milestones;
    } else {
      // By milestone - show only milestones with entries
      return milestones;
    }
  };

  return (
    <>
      <Card id="daily-diary" sx={{ mb: 3 }}>
        <CardContent>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Daily Diary
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh">
                <IconButton onClick={handleRefresh} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>

              {canPost && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenDialog}
                  sx={{
                    background: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)',
                    color: '#fff'
                  }}
                >
                  Add Daily Update
                </Button>
              )}
            </Box>
          </Box>

          {/* Description */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {canPost
              ? 'Share daily progress updates with photos and videos to keep your client informed'
              : 'View daily progress updates from your project team'
            }
          </Typography>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="All Entries" />
              <Tab label="By Milestone" />
            </Tabs>
          </Box>

          {/* Milestone Filter (shown in "By Milestone" tab) */}
          {activeTab === 1 && getFilteredMilestones().length > 0 && (
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Filter by Milestone</InputLabel>
                <Select
                  value={selectedMilestone}
                  onChange={handleMilestoneFilterChange}
                  label="Filter by Milestone"
                  startAdornment={<FilterIcon sx={{ ml: 1, mr: -0.5, color: 'text.secondary' }} />}
                >
                  <MenuItem value="">
                    <em>All Milestones</em>
                  </MenuItem>
                  {getFilteredMilestones().map((milestone) => (
                    <MenuItem key={milestone.id} value={milestone.id}>
                      {milestone.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error State */}
          {error && !loading && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Empty State */}
          {!loading && !error && entries.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                textAlign: 'center'
              }}
            >
              <CalendarIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No diary entries yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {canPost
                  ? 'Start posting daily updates to keep your client informed'
                  : 'Your project team hasn\'t posted any updates yet'
                }
              </Typography>
              {canPost && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenDialog}
                  sx={{
                    background: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)',
                    color: '#fff'
                  }}
                >
                  Post Your First Update
                </Button>
              )}
            </Box>
          )}

          {/* Entry List */}
          {!loading && !error && entries.length > 0 && (
            <>
              <Box>
                {entries.map((entry) => (
                  <DailyEntryCard
                    key={entry.id}
                    entry={entry}
                    onPhotoClick={(photoUrl) => {
                      // Photo lightbox is handled within DailyEntryCard
                    }}
                  />
                ))}
              </Box>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Entry Dialog */}
      {canPost && (
        <AddDailyEntryDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          projectId={projectId}
          project={project}
          milestones={milestones}
          onEntryCreated={handleEntryCreated}
        />
      )}
    </>
  );
};

export default DailyDiarySection;
